import { useState, useEffect, useMemo } from 'react';
import styles from './index.module.scss';
import aixueai4Bg1 from '@/assets/aixueai4-bg1.jpg';
import aixueai4Bg2 from '@/assets/aixueai4-bg2.jpg';
import aixueai4Bg3 from '@/assets/aixueai4-bg3.jpg';

// 屏幕配置常量
const SCREEN_CONFIG = {
  small: {
    activeCardWidth: 775,     // 激活卡片宽度
    containerPadding: 20,     // 容器左右padding
    cardSpacing: 745,         // 卡片间距 (701 + 44)
  },
  large: {
    activeCardWidth: 996,     // 激活卡片宽度
    containerPadding: 40,     // 容器左右padding
    cardSpacing: 957,         // 卡片间距 (901 + 56)
  },
  scrollBarWidth: 17
};

const isWindows = () => { 
  return /windows|win32/i.test(navigator.userAgent);
};

const isEdge = () => {
  return /Edg|Edge/.test(navigator.userAgent);
}

export default function Screen4() {
  const [currentCard, setCurrentCard] = useState(1);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  // 检测屏幕尺寸
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
    
    return () => window.removeEventListener('resize', updateWindowSize);
  }, []);

  // 判断是否为大屏
  const isLargeScreen = useMemo(() => windowSize.height >= 800, [windowSize.height]);

  // 获取当前屏幕配置
  const config = useMemo(() => 
    isLargeScreen ? SCREEN_CONFIG.large : SCREEN_CONFIG.small, 
    [isLargeScreen]
  );

  // 计算transform值
  const transformValue = useMemo(() => {
    if (windowSize.width === 0) return 'translateX(0)';
    
    const { activeCardWidth, containerPadding, cardSpacing } = config;
    
    // 计算屏幕中心位置
    const screenCenter = windowSize.width / 2;
    
    // 计算第一个卡片的位置（考虑容器padding）
    const firstCardLeft = containerPadding;
    
    // 计算当前活跃卡片的位置
    const currentCardLeft = firstCardLeft + currentCard * cardSpacing;
    const currentCardCenter = currentCardLeft + activeCardWidth / 2;
    // 计算滚动条占据的空间大小，mac不占空间，windows上占空间，windows且Edge有俩滚动条需要减去双份
    const scrollBarWidth = isWindows() ? isEdge() ? SCREEN_CONFIG.scrollBarWidth : (SCREEN_CONFIG.scrollBarWidth / 2) : 0;
    // 计算需要移动的距离，使当前活跃卡片中心对齐屏幕中心
    const moveDistance = screenCenter - currentCardCenter - scrollBarWidth;
    
    return `translateX(${moveDistance}px)`;
  }, [currentCard, windowSize.width, config]);

  // 卡片数据
  const cards = useMemo(() => [
    {
      id: 1,
      title: '多模态感知能力',
      description: '通过情绪、状态识别，提供情感化的互动体验，增加用户的参与感',
      rightContent: 'ai_logo'
    },
    {
      id: 2,
      title: '极致的拟真教学沟通',
      description: '运用多情感TTS及跨语言技术，提供真实、无障碍的教学沟通体验',
      rightContent: 'robot_arm'
    },
    {
      id: 3,
      title: '多场景智能内容生成',
      description: '在多个场景下实时生成个性化内容，灵活适配用户反馈，强化学习效果与体验',
      rightContent: 'simple_bg'
    }
  ], []);

  // 处理卡片点击
  const handleCardClick = (index: number) => {
    setCurrentCard(index);
  };

  // 处理上一张
  const handlePrev = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
    }
  };

  // 处理下一张
  const handleNext = () => {
    if (currentCard < cards.length - 1) {
      setCurrentCard(currentCard + 1);
    }
  };

  // 处理分页器点击
  const handleDotClick = (index: number) => {
    setCurrentCard(index);
  };

  // 按钮禁用状态
  const isPrevDisabled = currentCard === 0;
  const isNextDisabled = currentCard === cards.length - 1;

  // 渲染右侧内容
  const renderRightContent = (rightContent: string) => {
    switch (rightContent) {
      case 'robot_arm':
        return (
          <>
            <img className={styles['screen4-image']} src={aixueai4Bg1} alt="robot_arm" />
          </>
        );
      case 'ai_logo':
        return (
          <>
            <img className={styles['screen4-image']} src={aixueai4Bg2} alt="ai_logo" />
          </>
        );
      case 'simple_bg':
        return <img className={styles['screen4-image']} src={aixueai4Bg3} alt="simple_bg" />;
      default:
        return null;
    }
  };

  return (
    <section className={styles['screen4-cover']}>
      <div className={styles['screen4-wrap']}>
        {/* 主标题 */}
        <h2 className={styles['screen4-title']}>
          全自研多场景数字人
        </h2>
        
        {/* 副标题 */}
        <div className={styles['screen4-subtitle']}>
          融合多模态感知与智能语音交互能力，创造革命性的AI互动体验
        </div>

        {/* 左右切换按钮 */}
        <div className={styles['screen4-nav']}>
          <button
            className={`${styles['screen4-nav-btn']} ${isPrevDisabled ? styles['screen4-nav-btn--disabled'] : ''}`}
            onClick={handlePrev}
            disabled={isPrevDisabled}
            aria-label="上一张"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className={`${styles['screen4-nav-btn']} ${isNextDisabled ? styles['screen4-nav-btn--disabled'] : ''}`}
            onClick={handleNext}
            disabled={isNextDisabled}
            aria-label="下一张"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* 卡片容器 */}
        <div className={styles['screen4-cards-container']}>
          <div 
            className={styles['screen4-cards-track']}
            style={{ 
              transform: transformValue
            }}
          >
            {cards.map((card, index) => (
              <div 
                key={card.id} 
                className={`${styles['screen4-card']} ${
                  index === currentCard ? styles['screen4-card-active'] : ''
                }`}
                onClick={() => handleCardClick(index)}
              >
                <div className={styles['screen4-card-content']}>
                  {/* 左侧内容 */}
                  <div className={styles['screen4-card-left']}>
                    <h3 className={styles['screen4-card-title']}>{card.title}</h3>
                    <p className={styles['screen4-card-desc']}>{card.description}</p>
                  </div>
                  
                  {/* 右侧图片 */}
                  <div 
                    className={styles['screen4-card-right']}
                  >
                    {renderRightContent(card.rightContent)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* 分页器 */}
        <div className={styles['screen4-pagination']}>
          <div className={styles['screen4-pagination-row']}>
            {cards.map((_, index) => (
              <span
                key={index}
                className={index === currentCard ? styles['screen4-bar'] : styles['screen4-dot']}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
